import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

const STATUS_CLASS = {
  draft: "muted",
  proposed: "warn",
  accepted: "good",
  declined: "bad",
  active: "good",
  completed: "good",
  cancelled: "muted",
};

const MS_STATUS_CLASS = {
  pending: "muted",
  in_progress: "warn",
  delivered: "warn",
  approved: "good",
  paid: "good",
};

function formatAmount(cents, currency) {
  if (!cents) return "—";
  return (cents / 100).toLocaleString(undefined, { style: "currency", currency: currency || "CAD" });
}

export default function MandateDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingMs, setEditingMs] = useState(false);
  const [milestoneDraft, setMilestoneDraft] = useState([]);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerForm, setHeaderForm] = useState(null);
  const [deliverFor, setDeliverFor] = useState(null);
  const [deliverForm, setDeliverForm] = useState({ deliverable_url: "", deliverable_note: "" });
  const [declineFor, setDeclineFor] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/mandates/${id}`);
      setM(r.data);
      setMilestoneDraft(r.data.milestones || []);
      setHeaderForm({
        title: r.data.title,
        scope: r.data.scope,
        currency: r.data.currency,
        total_amount_cents: r.data.total_amount_cents,
        start_date: r.data.start_date || "",
        end_date: r.data.end_date || "",
      });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!user) return <div className="tb-loading">{t("common.loading")}</div>;
  if (err && !m) return <div className="tb-page"><div className="tb-form-error">{err}</div></div>;
  if (!m) return <div className="tb-loading">{t("common.loading")}</div>;

  const isAdvisor = m.advisor_id === user.id || user.role === "admin";
  const isFounder = m.founder_id === user.id || user.team_owner_id === m.founder_id || user.role === "admin";
  const isCreator = m.created_by === user.id;
  const editable = (m.status === "draft" || m.status === "proposed") && (isCreator || user.role === "admin");

  const act = async (path, body) => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/mandates/${id}${path}`, body || {});
      setM(r.data);
      setMilestoneDraft(r.data.milestones || []);
      return r.data;
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const saveHeader = async () => {
    setBusy(true); setErr("");
    try {
      const body = { ...headerForm, total_amount_cents: Number(headerForm.total_amount_cents) || 0,
        start_date: headerForm.start_date || null, end_date: headerForm.end_date || null };
      const r = await api.put(`/mandates/${id}`, body);
      setM({ ...m, ...r.data });
      setEditingHeader(false);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const saveMilestones = async () => {
    setBusy(true); setErr("");
    try {
      const ms = milestoneDraft.map((s) => ({
        title: s.title, description: s.description || "",
        amount_cents: Number(s.amount_cents) || 0,
        due_date: s.due_date || null,
      }));
      const r = await api.put(`/mandates/${id}`, { milestones: ms });
      setM({ ...m, ...r.data });
      setMilestoneDraft(r.data.milestones || []);
      setEditingMs(false);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const deliver = async (sid) => {
    const r = await act(`/milestones/${sid}/deliver`, deliverForm);
    if (r) { setDeliverFor(null); setDeliverForm({ deliverable_url: "", deliverable_note: "" }); }
  };

  const counterparty = isAdvisor && !user.role === "admin" ? m.founder : m.advisor;

  return (
    <div className="tb-page">
      <Link to="/app/bookings" className="tb-muted" data-testid="back-link" style={{ fontSize: "0.85rem" }}>← {t("common.back")}</Link>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, flexWrap: "wrap", marginTop: 8 }}>
        <h1 className="tb-h1" data-testid="mandate-title">{m.title}</h1>
        <span className={`tb-badge ${STATUS_CLASS[m.status] || "muted"}`} data-testid="mandate-status">{t(`mandates.status.${m.status}`)}</span>
      </div>

      {err && <div className="tb-form-error" style={{ margin: "8px 0" }} data-testid="mandate-error">{err}</div>}

      <div className="tb-block" style={{ marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <h3 style={{ marginTop: 0 }}>{t("mandates.summary")}</h3>
          {editable && !editingHeader && (
            <button className="tb-ghost-btn" onClick={() => setEditingHeader(true)} data-testid="edit-header-btn">{t("mandates.edit")}</button>
          )}
        </div>

        {!editingHeader && (
          <>
            <div className="tb-muted" style={{ marginBottom: 6 }} data-testid="mandate-parties">
              <strong>{m.advisor?.name || t("mandates.advisor")}</strong>
              {m.advisor?.company ? ` · ${m.advisor.company}` : ""}
              {" ↔ "}
              <strong>{m.founder?.name || t("mandates.entrepreneur")}</strong>
              {m.founder?.company ? ` · ${m.founder.company}` : ""}
            </div>
            <div style={{ whiteSpace: "pre-wrap", marginBottom: 12 }} data-testid="mandate-scope">{m.scope || <span className="tb-muted">—</span>}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: "0.9rem" }}>
              <div><span className="tb-muted">{t("mandates.totalAmount")}:</span> <strong>{formatAmount(m.total_amount_cents, m.currency)}</strong></div>
              <div data-testid="mandate-start-date"><span className="tb-muted">{t("mandates.startDate")}:</span> {m.start_date || "—"}</div>
              <div data-testid="mandate-end-date"><span className="tb-muted">{t("mandates.endDate")}:</span> {m.end_date || "—"}</div>
            </div>
          </>
        )}

        {editingHeader && headerForm && (
          <div className="tb-form">
            <div><label>{t("mandates.titleLabel")}</label><input type="text" value={headerForm.title} onChange={(e) => setHeaderForm({ ...headerForm, title: e.target.value })} data-testid="edit-title" /></div>
            <div><label>{t("mandates.scope")}</label><textarea rows={4} value={headerForm.scope} onChange={(e) => setHeaderForm({ ...headerForm, scope: e.target.value })} data-testid="edit-scope" /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
              <div>
                <label>{t("mandates.currency")}</label>
                <select value={headerForm.currency} onChange={(e) => setHeaderForm({ ...headerForm, currency: e.target.value })}>
                  <option>CAD</option><option>USD</option><option>EUR</option>
                </select>
              </div>
              <div><label>{t("mandates.totalAmount")}</label><input type="number" min="0" step="100" value={Math.round((headerForm.total_amount_cents || 0) / 100)} onChange={(e) => setHeaderForm({ ...headerForm, total_amount_cents: (Number(e.target.value) || 0) * 100 })} data-testid="edit-amount" /></div>
              <div><label>{t("mandates.startDate")}</label><input type="date" value={headerForm.start_date} onChange={(e) => setHeaderForm({ ...headerForm, start_date: e.target.value })} /></div>
              <div><label>{t("mandates.endDate")}</label><input type="date" value={headerForm.end_date} onChange={(e) => setHeaderForm({ ...headerForm, end_date: e.target.value })} /></div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="tb-primary-btn" onClick={saveHeader} disabled={busy} data-testid="save-header-btn">{t("mandates.save")}</button>
              <button className="tb-ghost-btn" onClick={() => { setEditingHeader(false); setHeaderForm({ ...m }); }}>{t("book.cancel")}</button>
            </div>
          </div>
        )}
      </div>

      {/* Lifecycle actions */}
      <div className="tb-block" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>{t("mandates.actions")}</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {m.status === "draft" && isCreator && (
            <button className="tb-primary-btn" onClick={() => act("/propose")} disabled={busy} data-testid="propose-btn">{t("mandates.propose")}</button>
          )}
          {m.status === "proposed" && !isCreator && (
            <>
              <button className="tb-primary-btn" onClick={() => act("/accept")} disabled={busy} data-testid="accept-btn">{t("mandates.accept")}</button>
              <button className="tb-ghost-btn" onClick={() => setDeclineFor(true)} disabled={busy} data-testid="decline-open-btn">{t("mandates.decline")}</button>
            </>
          )}
          {m.status === "active" && isFounder && (
            <button className="tb-primary-btn" onClick={() => act("/complete")} disabled={busy} data-testid="complete-btn">{t("mandates.markComplete")}</button>
          )}
          {!["completed", "cancelled", "declined"].includes(m.status) && (
            <button className="tb-ghost-btn" onClick={() => { if (window.confirm(t("mandates.confirmCancel"))) act("/cancel"); }} disabled={busy} data-testid="cancel-btn">{t("mandates.cancelMandate")}</button>
          )}
          {(user.role === "accelerator" || user.role === "admin") && !m.archived && (
            <button className="tb-ghost-btn" onClick={() => act("/archive")} disabled={busy} data-testid="archive-mandate-btn">{t("funnel.archive")}</button>
          )}
        </div>

        {declineFor && (
          <div style={{ marginTop: 12 }}>
            <label>{t("mandates.declineReason")}</label>
            <textarea rows={3} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} data-testid="decline-reason" />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button className="tb-primary-btn" onClick={async () => { await act("/decline", { reason: declineReason }); setDeclineFor(false); setDeclineReason(""); }} disabled={busy} data-testid="decline-submit-btn">{t("mandates.decline")}</button>
              <button className="tb-ghost-btn" onClick={() => setDeclineFor(false)}>{t("book.cancel")}</button>
            </div>
          </div>
        )}

        {m.decline_reason && m.status === "declined" && (
          <div className="tb-muted" style={{ marginTop: 12 }}><strong>{t("mandates.declineReason")}:</strong> {m.decline_reason}</div>
        )}
      </div>

      {/* Milestones */}
      <div className="tb-block" style={{ marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h3 style={{ marginTop: 0 }}>{t("mandates.milestones")}</h3>
          {editable && !editingMs && (
            <button className="tb-ghost-btn" onClick={() => setEditingMs(true)} data-testid="edit-milestones-btn">{t("mandates.editMilestones")}</button>
          )}
        </div>

        {editingMs && editable && (
          <div>
            {milestoneDraft.map((s, idx) => (
              <div key={s.id || idx} className="tb-block" style={{ marginBottom: 10 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 140px auto", gap: 8 }}>
                  <input type="text" placeholder={t("mandates.milestoneTitle")} value={s.title || ""} onChange={(e) => { const x = [...milestoneDraft]; x[idx] = { ...x[idx], title: e.target.value }; setMilestoneDraft(x); }} data-testid={`ms-title-${idx}`} />
                  <input type="number" min="0" step="100" placeholder="Amount" value={Math.round((s.amount_cents || 0) / 100)} onChange={(e) => { const x = [...milestoneDraft]; x[idx] = { ...x[idx], amount_cents: (Number(e.target.value) || 0) * 100 }; setMilestoneDraft(x); }} />
                  <input type="date" value={s.due_date || ""} onChange={(e) => { const x = [...milestoneDraft]; x[idx] = { ...x[idx], due_date: e.target.value }; setMilestoneDraft(x); }} />
                  <button className="tb-funnel-del" onClick={() => setMilestoneDraft(milestoneDraft.filter((_, i) => i !== idx))} aria-label="remove">×</button>
                </div>
                <textarea rows={2} placeholder={t("mandates.milestoneDescription")} value={s.description || ""} onChange={(e) => { const x = [...milestoneDraft]; x[idx] = { ...x[idx], description: e.target.value }; setMilestoneDraft(x); }} style={{ marginTop: 6 }} />
              </div>
            ))}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="tb-ghost-btn" onClick={() => setMilestoneDraft([...milestoneDraft, { title: "", description: "", amount_cents: 0, due_date: "" }])} data-testid="add-ms-btn">+ {t("mandates.addMilestone")}</button>
              <button className="tb-primary-btn" onClick={saveMilestones} disabled={busy} data-testid="save-ms-btn">{t("mandates.save")}</button>
              <button className="tb-ghost-btn" onClick={() => { setMilestoneDraft(m.milestones || []); setEditingMs(false); }}>{t("book.cancel")}</button>
            </div>
          </div>
        )}

        {!editingMs && (
          (m.milestones || []).length === 0 ? (
            <p className="tb-muted">{t("mandates.noMilestones")}</p>
          ) : (
            <div>
              {m.milestones.map((s) => (
                <div key={s.id} className="tb-block" style={{ marginBottom: 10 }} data-testid={`ms-${s.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                    <strong>{s.title}</strong>
                    <span className={`tb-badge ${MS_STATUS_CLASS[s.status] || "muted"}`} data-testid={`ms-status-${s.id}`}>{t(`mandates.milestoneStatus.${s.status}`)}</span>
                  </div>
                  {s.description && <div style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{s.description}</div>}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: "0.85rem", marginTop: 8 }}>
                    <span>{formatAmount(s.amount_cents, m.currency)}</span>
                    {s.due_date && <span className="tb-muted">{t("mandates.due")}: {s.due_date}</span>}
                    {s.delivered_at && <span className="tb-muted">{t("mandates.deliveredOn")}: {s.delivered_at.slice(0, 10)}</span>}
                    {s.approved_at && <span className="tb-muted">{t("mandates.approvedOn")}: {s.approved_at.slice(0, 10)}</span>}
                  </div>
                  {s.deliverable_url && (
                    <div style={{ marginTop: 6, fontSize: "0.85rem" }}>
                      <a href={s.deliverable_url} target="_blank" rel="noreferrer" data-testid={`ms-deliverable-${s.id}`}>{t("mandates.viewDeliverable")} ↗</a>
                    </div>
                  )}
                  {s.deliverable_note && <div className="tb-muted" style={{ marginTop: 4, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>{s.deliverable_note}</div>}

                  {m.status === "active" && (
                    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                      {isAdvisor && s.status === "pending" && (
                        <button className="tb-ghost-btn" onClick={() => act(`/milestones/${s.id}/start`)} disabled={busy} data-testid={`ms-start-${s.id}`}>{t("mandates.startMilestone")}</button>
                      )}
                      {isAdvisor && (s.status === "pending" || s.status === "in_progress") && (
                        <button className="tb-primary-btn" onClick={() => { setDeliverFor(s.id); setDeliverForm({ deliverable_url: s.deliverable_url || "", deliverable_note: s.deliverable_note || "" }); }} disabled={busy} data-testid={`ms-deliver-${s.id}`}>{t("mandates.deliverMilestone")}</button>
                      )}
                      {isFounder && s.status === "delivered" && (
                        <button className="tb-primary-btn" onClick={() => act(`/milestones/${s.id}/approve`)} disabled={busy} data-testid={`ms-approve-${s.id}`}>{t("mandates.approveMilestone")}</button>
                      )}
                    </div>
                  )}

                  {deliverFor === s.id && (
                    <div style={{ marginTop: 12, padding: 12, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }}>
                      <div>
                        <label>{t("mandates.deliverableUrl")}</label>
                        <input type="url" value={deliverForm.deliverable_url} onChange={(e) => setDeliverForm({ ...deliverForm, deliverable_url: e.target.value })} placeholder="https://…" data-testid={`deliver-url-${s.id}`} />
                      </div>
                      <div>
                        <label>{t("mandates.deliverableNote")}</label>
                        <textarea rows={2} value={deliverForm.deliverable_note} onChange={(e) => setDeliverForm({ ...deliverForm, deliverable_note: e.target.value })} data-testid={`deliver-note-${s.id}`} />
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <button className="tb-primary-btn" onClick={() => deliver(s.id)} disabled={busy} data-testid={`deliver-submit-${s.id}`}>{t("mandates.submitDelivery")}</button>
                        <button className="tb-ghost-btn" onClick={() => setDeliverFor(null)}>{t("book.cancel")}</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
